/**
 * ISO 9001 Self-Gap Assessment Page
 * Run, view, and download ISO 9001:2015 self-assessments.
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import QMSLayout from "../components/QMSLayout";
import {
  Shield, Plus, Download, CheckCircle2, AlertTriangle, XCircle,
  Loader2, RefreshCw, ChevronDown, ChevronRight, BarChart3, FileText, Activity
} from "lucide-react";

const STATUS_COLORS = {
  met: "text-emerald-400 bg-emerald-500/10",
  partial: "text-amber-400 bg-amber-500/10",
  unmet: "text-red-400 bg-red-500/10",
};

export default function ISO9001Page() {
  const { user } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [detail, setDetail] = useState(null);
  const [expandedClause, setExpandedClause] = useState(null);
  const [digests, setDigests] = useState([]);
  const [digestLoading, setDigestLoading] = useState(false);
  const [generatingDigest, setGeneratingDigest] = useState(false);
  const isAdmin = user?.role === "admin";

  const fetchAssessments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/iso9001/assessments`, { credentials: "include" });
      const data = await res.json();
      setAssessments(data.assessments || []);
    } catch { setAssessments([]); }
    setLoading(false);
  }, []);

  const fetchDigests = useCallback(async () => {
    setDigestLoading(true);
    try {
      const res = await fetch(`${API}/qms-health-digest/reports`, { credentials: "include" });
      const data = await res.json();
      setDigests(data.digests || []);
    } catch { setDigests([]); }
    setDigestLoading(false);
  }, []);

  useEffect(() => { fetchAssessments(); fetchDigests(); }, [fetchAssessments, fetchDigests]);

  const runAssessment = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API}/iso9001/self-assessment`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        fetchAssessments();
        loadDetail(data.assessment_id);
      }
    } catch {}
    setRunning(false);
  };

  const loadDetail = async (id) => {
    try {
      const res = await fetch(`${API}/iso9001/assessments/${id}`, { credentials: "include" });
      if (res.ok) setDetail(await res.json());
    } catch {}
  };

  const handleDownload = async (id) => {
    const res = await fetch(`${API}/iso9001/assessments/${id}/download`, { credentials: "include" });
    if (res.ok) {
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `ISO9001_Self_Assessment_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  const generateDigest = async () => {
    setGeneratingDigest(true);
    try {
      const res = await fetch(`${API}/qms-health-digest/generate`, { method: "POST", credentials: "include" });
      if (res.ok) fetchDigests();
    } catch {}
    setGeneratingDigest(false);
  };

  const downloadDigest = async (id) => {
    const res = await fetch(`${API}/qms-health-digest/reports/${id}/download`, { credentials: "include" });
    if (res.ok) {
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `QMS_Health_Digest_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  return (
    <QMSLayout>
      <div className="space-y-4" data-testid="iso9001-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-white flex items-center gap-2">
              <Shield size={20} className="text-sky-400" />
              ISO 9001:2015 Self-Gap Assessment
            </h1>
            <p className="text-xs text-[#8B95A5] mt-0.5">Self-assessment against ISO 9001:2015 clauses 4-10, filed in Document Control</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchAssessments} className="p-2 rounded bg-[#1A1D27] border border-[rgba(255,255,255,0.07)] text-[#8B95A5] hover:text-white">
              <RefreshCw size={14} />
            </button>
            {isAdmin && (
              <button onClick={runAssessment} disabled={running} className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded bg-sky-500/10 text-sky-400 border border-sky-500/20 hover:bg-sky-500/20 transition-all disabled:opacity-40" data-testid="run-assessment-btn">
                {running ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                Run Self-Assessment
              </button>
            )}
          </div>
        </div>

        {/* Assessment List */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-sky-400" /></div>
        ) : assessments.length === 0 ? (
          <div className="text-center py-16" data-testid="no-assessments-msg">
            <Shield size={40} className="text-[#333] mx-auto mb-3" />
            <p className="text-[#8B95A5] text-sm">No ISO 9001 self-assessments have been run yet.</p>
            {isAdmin && <p className="text-[#555] text-xs mt-1">Click "Run Self-Assessment" to generate the first report.</p>}
          </div>
        ) : (
          <div className="space-y-3" data-testid="assessment-list">
            {assessments.map(a => (
              <div key={a.assessment_id} className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg p-4 cursor-pointer hover:border-sky-500/20 transition-all" onClick={() => loadDetail(a.assessment_id)} data-testid={`assessment-${a.assessment_id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-lg font-bold ${
                      a.overall_score >= 80 ? "bg-emerald-500/10 text-emerald-400" :
                      a.overall_score >= 60 ? "bg-amber-500/10 text-amber-400" :
                      "bg-red-500/10 text-red-400"
                    }`}>{a.overall_score}%</div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium text-sm">{a.standard}</span>
                        <span className="text-[#8B95A5] font-mono text-xs">{a.assessment_id}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-[#8B95A5]">
                        <span className="flex items-center gap-1"><CheckCircle2 size={10} className="text-emerald-400" />{a.met} met</span>
                        <span className="flex items-center gap-1"><AlertTriangle size={10} className="text-amber-400" />{a.partial} partial</span>
                        <span className="flex items-center gap-1"><XCircle size={10} className="text-red-400" />{a.unmet} unmet</span>
                        <span>{a.generated_at?.slice(0, 10)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                    <button onClick={() => handleDownload(a.assessment_id)} className="flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-sky-500/10 text-sky-400 border border-sky-500/20 hover:bg-sky-500/20" data-testid={`download-${a.assessment_id}`}>
                      <Download size={12} />Download PDF
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Quarterly QMS Health Digest */}
        <div className="mt-6" data-testid="health-digest-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <Activity size={16} className="text-emerald-400" />
              Quarterly QMS Health Digest
            </h2>
            {isAdmin && (
              <button onClick={generateDigest} disabled={generatingDigest} className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all disabled:opacity-40" data-testid="generate-digest-btn">
                {generatingDigest ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
                Generate Health Digest
              </button>
            )}
          </div>
          {digestLoading ? (
            <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-emerald-400" /></div>
          ) : digests.length === 0 ? (
            <div className="text-center py-8 bg-[#181B23] rounded-lg border border-[rgba(255,255,255,0.07)]" data-testid="no-digests-msg">
              <Activity size={32} className="text-[#333] mx-auto mb-2" />
              <p className="text-[#8B95A5] text-sm">No health digests generated yet.</p>
              {isAdmin && <p className="text-[#555] text-xs mt-1">Click "Generate Health Digest" to create the first quarterly report.</p>}
            </div>
          ) : (
            <div className="space-y-2" data-testid="digest-list">
              {digests.map(d => (
                <div key={d.digest_id} className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg p-3 flex items-center justify-between hover:border-emerald-500/20 transition-all" data-testid={`digest-${d.digest_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                      <Activity size={16} className="text-emerald-400" />
                    </div>
                    <div>
                      <span className="text-white text-sm font-medium">{d.quarter}</span>
                      <span className="text-[#8B95A5] font-mono text-xs ml-2">{d.digest_id}</span>
                      <div className="text-xs text-[#8B95A5] mt-0.5">
                        Generated {d.generated_at?.slice(0, 10)} by {d.generated_by_name}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => downloadDigest(d.digest_id)} className="flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20" data-testid={`download-digest-${d.digest_id}`}>
                    <Download size={12} />Download PDF
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {detail && (
          <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg p-5 mt-4" data-testid="assessment-detail">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <BarChart3 size={18} className="text-sky-400" />
                Clause Breakdown — {detail.assessment_id}
              </h2>
              <button onClick={() => setDetail(null)} className="text-[#8B95A5] hover:text-white text-sm">Close</button>
            </div>
            <div className="space-y-2">
              {(detail.clause_results || []).map(cr => (
                <div key={cr.clause} className="bg-[#1A1D27] rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-[rgba(255,255,255,0.02)]"
                    onClick={() => setExpandedClause(expandedClause === cr.clause ? null : cr.clause)}>
                    <div className="flex items-center gap-3">
                      {expandedClause === cr.clause ? <ChevronDown size={14} className="text-[#555]" /> : <ChevronRight size={14} className="text-[#555]" />}
                      <span className="text-sky-400 font-mono text-sm">Clause {cr.clause}</span>
                      <span className="text-white text-sm">{cr.title}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className={`w-16 h-2 rounded-full bg-[rgba(255,255,255,0.05)] overflow-hidden`}>
                        <div className={`h-full rounded-full ${cr.score >= 80 ? "bg-emerald-500" : cr.score >= 60 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${cr.score}%` }} />
                      </div>
                      <span className={`text-sm font-medium ${cr.score >= 80 ? "text-emerald-400" : cr.score >= 60 ? "text-amber-400" : "text-red-400"}`}>{cr.score}%</span>
                    </div>
                  </div>
                  {expandedClause === cr.clause && (
                    <div className="px-3 pb-3 space-y-1.5">
                      {cr.requirements.map(req => (
                        <div key={req.req_id} className="flex items-start gap-2 p-2 rounded bg-[rgba(255,255,255,0.02)]">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium mt-0.5 ${STATUS_COLORS[req.status]}`}>
                            {req.status.toUpperCase()}
                          </span>
                          <div className="flex-1">
                            <span className="text-xs text-sky-400 font-mono">{req.req_id}</span>
                            <span className="text-xs text-white ml-2">{req.title}</span>
                            {req.evidence && <p className="text-xs text-[#8B95A5] mt-0.5">{req.evidence}</p>}
                            {req.gap && <p className="text-xs text-red-400 mt-0.5">Gap: {req.gap}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </QMSLayout>
  );
}
